import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Eye, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { DataTable } from "../components/DataTable";
import { StatusBadge } from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { fmtDate, fmtMoney, titleCase } from "../lib/format";

const STATUSES = ["pending_review", "processing", "refunded", "rejected", "all"];

export default function RefundRequests() {
  const [data, setData] = useState({ items: [], total: 0 });
  const [status, setStatus] = useState("pending_review");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [decision, setDecision] = useState(null);
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [internalNote, setInternalNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, per_page: 25 };
      if (status !== "all") params.status = status;
      const response = await api.get("/admin/refund-requests", { params });
      setData(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load refund requests");
    } finally {
      setLoading(false);
    }
  }, [page, status]);

  useEffect(() => { load(); }, [load]);

  const openDecision = (type, row) => {
    if (!row.supplier_response) return;
    setDecision({ type, row });
    setAmount(String(row.requested_amount));
    setNote("");
  };

  const openDetail = async (row) => {
    setDetailLoading(true);
    setSelected(row);
    setInternalNote("");
    try {
      const response = await api.get(`/admin/refund-requests/${row.id}`);
      setSelected(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load refund request details");
      setSelected(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const submitDecision = async () => {
    if (!decision?.row.supplier_response) return;
    const isApproval = decision.type === "approve";
    if (!isApproval && !note.trim()) return;
    const parsedAmount = Number(amount);
    if (isApproval && (!Number.isFinite(parsedAmount) || parsedAmount <= 0)) return;

    setSaving(true);
    try {
      const path = `/admin/refund-requests/${decision.row.id}/${isApproval ? "approve" : "reject"}`;
      const body = isApproval
        ? { approved_amount: parsedAmount, note: note.trim() || null }
        : { reason: note.trim() };
      await api.post(path, body);
      toast.success(isApproval ? "Refund sent to Stripe for processing" : "Refund request rejected");
      setDecision(null);
      if (selected?.id === decision.row.id) await openDetail(decision.row);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Refund action failed");
    } finally {
      setSaving(false);
    }
  };

  const addInternalNote = async () => {
    if (!selected || !internalNote.trim()) return;
    setSavingNote(true);
    try {
      const response = await api.post(`/admin/refund-requests/${selected.id}/notes`, { note: internalNote.trim() });
      setSelected(response.data);
      setInternalNote("");
      toast.success("Internal note added");
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add note");
    } finally {
      setSavingNote(false);
    }
  };

  const columns = [
    {
      key: "booking", header: "Booking", render: (row) => (
        <div className="min-w-[160px]">
          <Link className="font-mono text-xs hover:underline" to={`/bookings/${row.booking?.id}`}>{row.booking?.reference || "—"}</Link>
          <div className="mt-0.5 max-w-[240px] truncate text-xs text-muted-foreground">{row.booking?.product_title}</div>
        </div>
      ),
    },
    {
      key: "customer", header: "Customer", render: (row) => (
        <div className="text-xs"><div>{row.customer?.full_name || "—"}</div><div className="text-muted-foreground">{row.customer?.email}</div></div>
      ),
    },
    { key: "requested_amount", header: "Requested", render: (row) => <span className="text-sm font-medium">{fmtMoney(row.requested_amount, row.currency)}</span> },
    { key: "reason", header: "Reason", render: (row) => <span className="block max-w-[220px] truncate text-xs" title={row.reason}>{row.reason}</span> },
    { key: "status", header: "Status", render: (row) => <StatusBadge status={row.status} /> },
    { key: "supplier_response", header: "Partner response", render: (row) => <span className="text-xs">{row.supplier_response ? titleCase(row.supplier_response.position) : row.status === "pending_review" ? "Awaiting partner response" : "No partner response"}</span> },
    { key: "created_at", header: "Created", render: (row) => <span className="text-xs text-muted-foreground">{fmtDate(row.created_at, true)}</span> },
    {
      key: "actions", header: "", render: (row) => (
        <div className="flex justify-end"><Button size="sm" variant="outline" onClick={(event) => { event.stopPropagation(); openDetail(row); }} data-testid={`refund-review-${row.id}`}><Eye className="w-3.5 h-3.5" /> Review</Button></div>
      ),
    },
  ];

  const isApproval = decision?.type === "approve";
  return (
    <div data-testid="refund-requests-page">
      <PageHeader title="Refund Requests" description="Review operational refund requests before a Stripe refund is created." />
      <div className="pt-6">
        <Button variant="outline" size="sm" className="mb-3" onClick={load} disabled={loading}>Refresh</Button>
        <DataTable
          columns={columns}
          rows={data.items}
          total={data.total}
          page={page}
          pageSize={25}
          loading={loading}
          onPageChange={setPage}
          toolbar={
            <Select value={status} onValueChange={(value) => { setStatus(value); setPage(1); }}>
              <SelectTrigger className="h-9 w-[190px] text-sm" data-testid="refund-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map((value) => <SelectItem key={value} value={value}>{value === "all" ? "All statuses" : titleCase(value)}</SelectItem>)}</SelectContent>
            </Select>
          }
          emptyTitle="No refund requests"
          testIdPrefix="refund-requests"
          onRowClick={openDetail}
        />
      </div>

      <Dialog open={!!selected} onOpenChange={(open) => { if (!open && !savingNote) setSelected(null); }}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto" data-testid="refund-details-dialog">
          <DialogHeader>
            <DialogTitle>Refund request details</DialogTitle>
            <DialogDescription>{selected?.booking?.reference || "Booking"} · {selected?.booking?.product_title || ""}</DialogDescription>
          </DialogHeader>
          <Button variant="outline" size="sm" onClick={() => selected && openDetail(selected)} disabled={detailLoading || savingNote}>Refresh request</Button>
          {detailLoading ? <div className="py-10 text-center text-sm text-muted-foreground">Loading request details…</div> : selected && <div className="space-y-5">
            <div className="grid grid-cols-1 gap-3 rounded-lg border border-border bg-muted/20 p-4 sm:grid-cols-2">
              <Detail label="Status" value={<StatusBadge status={selected.status} />} />
              <Detail label="Requested amount" value={fmtMoney(selected.requested_amount, selected.currency)} />
              <Detail label="Customer" value={selected.customer?.full_name} />
              <Detail label="Customer email" value={selected.customer?.email} />
              <Detail label="Activity" value={`${selected.booking?.activity_date || "—"}${selected.booking?.activity_time ? ` · ${selected.booking.activity_time}` : ""}`} />
              <Detail label="Payment intent" value={<span className="break-all font-mono text-xs">{selected.payment?.stripe_payment_intent_id || "—"}</span>} />
            </div>
            <div>
              <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Customer reason</div>
              <p className="mt-1 text-sm">{selected.reason}</p>
            </div>
            <div>
              <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Customer details</div>
              <p className="mt-1 whitespace-pre-wrap rounded-lg border border-border bg-muted/20 p-3 text-sm">{selected.details || "No additional details were provided."}</p>
            </div>
            <section className="rounded-lg border border-border p-4" data-testid="refund-supplier-response">
              <h3 className="text-sm font-semibold">Partner response</h3>
              {selected.supplier_response ? <div className="mt-2 space-y-2 text-sm">
                <p>{selected.supplier_response.position === "support" ? "Supports refund" : "Disputes refund"}</p>
                <p className="whitespace-pre-wrap">{selected.supplier_response.justification}</p>
                <p className="text-xs text-muted-foreground">{selected.supplier_response.responder_name} · {fmtDate(selected.supplier_response.responded_at, true)}</p>
              </div> : <p className="mt-2 text-sm text-muted-foreground">Awaiting partner response. Approval and rejection are unavailable until the partner submits their position and justification.</p>}
            </section>
            <div className="border-t border-border pt-4">
              <div className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">Internal notes & review history</div>
              <div className="space-y-2">
                {(selected.notes || []).length === 0 && <p className="text-sm text-muted-foreground">No internal notes yet.</p>}
                {(selected.notes || []).map((item, index) => <div key={`${item.created_at}-${index}`} className="rounded-lg border border-border p-3 text-sm"><div className="mb-1 text-xs text-muted-foreground">{titleCase(item.type)} · {fmtDate(item.created_at, true)}{item.admin_user_id ? ` · ${item.admin_user_id}` : ""}</div><div className="whitespace-pre-wrap">{item.note}</div></div>)}
              </div>
              <div className="mt-3 space-y-2">
                <Textarea rows={3} value={internalNote} onChange={(event) => setInternalNote(event.target.value)} placeholder="Add an internal note (not sent to the supplier)" data-testid="refund-internal-note" />
                <Button size="sm" variant="outline" onClick={addInternalNote} disabled={savingNote || !internalNote.trim()} data-testid="refund-add-note">{savingNote ? "Saving…" : "Add note"}</Button>
              </div>
            </div>
            {selected.status === "pending_review" && <DialogFooter className="border-t border-border pt-4">
              <Button variant="outline" disabled={!selected.supplier_response} onClick={() => openDecision("reject", selected)} data-testid="refund-detail-reject"><XCircle className="w-4 h-4" /> Reject</Button>
              <Button disabled={!selected.supplier_response} onClick={() => openDecision("approve", selected)} data-testid="refund-detail-approve"><CheckCircle2 className="w-4 h-4" /> Approve</Button>
            </DialogFooter>}
          </div>}
        </DialogContent>
      </Dialog>

      <Dialog open={!!decision} onOpenChange={(open) => { if (!saving && !open) setDecision(null); }}>
        <DialogContent data-testid="refund-decision-dialog">
          <DialogHeader>
            <DialogTitle>{isApproval ? "Approve refund" : "Reject refund request"}</DialogTitle>
            <DialogDescription>
              {isApproval
                ? "This creates a Stripe refund. Stripe webhook processing will confirm the final outcome."
                : "The request will be closed without sending any funds through Stripe."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {isApproval && <div>
              <Label htmlFor="approved-refund-amount" className="text-xs">Refund amount ({decision?.row.currency})</Label>
              <Input id="approved-refund-amount" type="number" min="0.01" max={decision?.row.requested_amount} step="0.01" value={amount} onChange={(event) => setAmount(event.target.value)} data-testid="approved-refund-amount" />
              <p className="mt-1 text-xs text-muted-foreground">Cannot exceed the requested {fmtMoney(decision?.row.requested_amount, decision?.row.currency)}.</p>
            </div>}
            <div>
              <Label htmlFor="refund-decision-note" className="text-xs">{isApproval ? "Review note (optional)" : "Rejection reason *"}</Label>
              <Textarea id="refund-decision-note" rows={3} value={note} onChange={(event) => setNote(event.target.value)} placeholder={isApproval ? "Optional internal note" : "Required for audit history"} data-testid="refund-decision-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDecision(null)} disabled={saving}>Cancel</Button>
            <Button variant={isApproval ? "default" : "destructive"} onClick={submitDecision} disabled={saving || !decision?.row.supplier_response || (!isApproval && !note.trim())} data-testid="refund-decision-submit">
              {saving ? "Saving…" : isApproval ? "Approve and send to Stripe" : "Reject request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Detail({ label, value }) {
  return <div><div className="text-xs text-muted-foreground">{label}</div><div className="mt-0.5 text-sm">{value || "—"}</div></div>;
}
